import React, { useCallback, useEffect, useState } from 'react'

import {
  Box,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from '@material-ui/core'
import { ArrowBack, Search } from '@material-ui/icons'
import { isEmpty } from 'lodash'

import { LoadingFeedback } from 'components'
import CourseCard from '../Home/components/CourseCard'
import useAuthTraining from 'hooks/useAuthTraining'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import * as service from 'service'
import useStyles from './styles'

const ROWS_PER_PAGE_OPTIONS = [12, 24, 48]

const CategoryCourses = ({ match, history }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { portalToken: contextPortalToken } = useAuthTraining()

  const portalToken = match?.params?.portalToken || contextPortalToken
  const categoryId = match?.params?.categoryId

  const [courses, setCourses] = useState([])
  const [category, setCategory] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [order, setOrder] = useState('newest')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0])
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, order, rowsPerPage])

  const loadCourses = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await service.trainingPortal.courses.indexByCategory(
        categoryId,
        {
          page: page + 1,
          per_page: rowsPerPage,
          q: debouncedSearch || undefined,
          order,
        },
      )
      const data = response?.data || {}
      setCourses(data?.courses || [])
      setCategory(data?.category)
      setTotalCount(data?.meta?.totalCount ?? data?.courses?.length ?? 0)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao carregar os cursos.',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [categoryId, page, rowsPerPage, debouncedSearch, order, snackbar])

  useEffect(() => {
    loadCourses()
    // eslint-disable-next-line
  }, [categoryId, page, rowsPerPage, debouncedSearch, order])

  const handleBack = () => {
    history.push(`/training/${portalToken}/home`)
  }

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      <LoadingFeedback open={isLoading} />

      <Box className={classes.backRow}>
        <IconButton
          className={classes.backButton}
          onClick={handleBack}
          size="small"
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography className={classes.backLabel} onClick={handleBack}>
          Voltar ao início
        </Typography>
      </Box>

      <Box className={classes.header}>
        <Typography className={classes.title}>{category?.name}</Typography>
        <Typography className={classes.description}>
          {category?.description}
        </Typography>
      </Box>

      <Box className={classes.controls}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar Cursos..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className={classes.searchField}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Select
          variant="outlined"
          value={order}
          onChange={(event) => setOrder(event.target.value)}
          className={classes.orderSelect}
        >
          <MenuItem value="newest">Mais Recentes</MenuItem>
          <MenuItem value="oldest">Mais Antigos</MenuItem>
          <MenuItem value="az">A-Z</MenuItem>
          <MenuItem value="za">Z-A</MenuItem>
        </Select>
      </Box>

      {!isLoading && isEmpty(courses) ? (
        <Box className={classes.emptyBox}>
          <Typography className={classes.emptyText}>
            Nenhum curso encontrado para esta categoria.
          </Typography>
        </Box>
      ) : (
        <>
          <Box className={classes.grid}>
            {courses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                portalToken={portalToken}
              />
            ))}
          </Box>

          {totalCount > rowsPerPage && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Cursos por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count}`
              }
              className={classes.pagination}
            />
          )}
        </>
      )}
    </Container>
  )
}

export default CategoryCourses
